
import { useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventFlows, useDeleteEventFlow, useUpdateEventFlow, useUpdateEventFlowPriority, useBookingStatuses } from "@/hooks/useEventManagement";
import { useCommunicationTemplates } from "@/hooks/useEventSystem";
import { EventFlowItem } from "./EventFlowItem";
import { supabase } from "@/integrations/supabase/client";

// Helper function to determine which channels a template supports
const getTemplateChannels = (template: any): string[] => {
  if (!template) return [];
  
  const channels: string[] = [];
  
  // Email support
  if (template.email_subject_template?.trim() || template.email_content_template?.trim()) {
    channels.push('email');
  }
  
  // Message support (SMS/WhatsApp)
  if (template.sms_content_template?.trim() || template.whatsapp_content_template?.trim()) {
    channels.push('message');
  }
  
  // Webhook support
  if (template.webhook_url?.trim()) {
    channels.push('webhook');
  }
  
  return channels;
};

interface EventFlowsListProps {
  onEdit: (flowId: string) => void;
}

export function EventFlowsList({ onEdit }: EventFlowsListProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<string | null>(null);
  const [affectedQueueCount, setAffectedQueueCount] = useState(0);
  const [affectedLogsCount, setAffectedLogsCount] = useState(0);
  const [reordering, setReordering] = useState(false);

  const { data: flows, isLoading, error } = useEventFlows();
  const { data: templates } = useCommunicationTemplates();
  const { data: statuses } = useBookingStatuses();
  const deleteFlow = useDeleteEventFlow();
  const updateFlow = useUpdateEventFlow();
  const updateFlowPriority = useUpdateEventFlowPriority();

  const handleDelete = async (flowId: string) => {
    setFlowToDelete(flowId);
    
    // Count affected queue entries
    const { data: queueData } = await supabase
      .from('event_queue')
      .select('id')
      .eq('event_flow_id', flowId);
    
    // Count affected orchestration logs
    const { data: logsData } = await supabase
      .from('orchestration_logs')
      .select('id')
      .eq('event_flow_id', flowId);
    
    setAffectedQueueCount(queueData?.length || 0);
    setAffectedLogsCount(logsData?.length || 0);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (flowToDelete) {
      await deleteFlow.mutateAsync(flowToDelete);
      setDeleteDialogOpen(false);
      setFlowToDelete(null);
    }
  };

  const toggleActive = async (flowId: string, currentActive: boolean) => {
    await updateFlow.mutateAsync({
      id: flowId,
      updates: { is_active: !currentActive }
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !flows) return;

    const items = Array.from(flows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update priority based on new positions
    const updates = items.map((item, index) => ({
      id: item.id,
      priority: index + 1,
    }));

    setReordering(true);
    try {
      await updateFlowPriority.mutateAsync(updates);
    } finally {
      setReordering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        <p>Error loading event flows</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "Unknown error"}
        </p>
      </div>
    );
  }

  if (!flows || flows.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No event flows found</p>
        <p className="text-sm">Create your first event flow to get started</p>
      </div>
    );
  }

  return (
    <>
      <div>
        {reordering && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-sm text-blue-800">Updating event flow priority...</span>
          </div>
        )}
        
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="event-flows">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {flows.map((flow, index) => {
                  const template = templates?.find(t => t.id === flow.template_id);
                  const supportedChannels = getTemplateChannels(template);
                  const unsupportedChannels = flow.channels.filter(
                    ch => !supportedChannels.includes(ch)
                  );
                  
                  return (
                    <EventFlowItem
                      key={flow.id}
                      flow={flow}
                      index={index}
                      onEdit={onEdit}
                      onDelete={handleDelete}
                      onToggleActive={toggleActive}
                      reordering={reordering}
                      hasUnsupportedChannels={unsupportedChannels.length > 0}
                      unsupportedChannels={unsupportedChannels}
                      statuses={statuses as any}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event Flow</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete this event flow? This action cannot be undone
                and will stop automated communications for this trigger.
              </p>
              {(affectedQueueCount > 0 || affectedLogsCount > 0) && (
                <div className="text-amber-600 font-medium space-y-1">
                  <p>⚠️ This will also affect:</p>
                  {affectedQueueCount > 0 && (
                    <p className="text-sm ml-4">
                      • {affectedQueueCount} pending queue entr{affectedQueueCount === 1 ? 'y' : 'ies'} (will be deleted)
                    </p>
                  )}
                  {affectedLogsCount > 0 && (
                    <p className="text-sm ml-4">
                      • {affectedLogsCount} historical log{affectedLogsCount === 1 ? '' : 's'} (flow reference will be cleared)
                    </p>
                  )}
                </div>
              )}
              {affectedQueueCount === 0 && affectedLogsCount === 0 && (
                <p className="text-green-600">
                  ✓ No queue entries or logs will be affected by this deletion.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
